import React, { forwardRef } from 'react';
import { InputMask, type InputMaskProps, unformat } from '@react-input/mask';
import {
  Alert,
  AlertTitle,
  Button,
  CardActions,
  CardContent,
  Snackbar,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { saveQrcodeFeedback } from '../../../api';
import { API_RESPONSE_STATUS_CODE } from '../../../constants/app';
import {
  Field,
  type Value,
  type Feedback,
} from '../../../interfaces/qrcode-feedback.type';
import type { IQrcodeDoctor } from '../../../interfaces/qrcode-doctor.type';

type IProps = {
  doctor: IQrcodeDoctor;
  goClaim: VoidFunction;
  feedback: Feedback;
  onChange: (f: Field) => (v: Value) => void;
};

const EMPTY_ERROR = { title: '', message: '' };

const maskOptions = {
  mask: '+7 9__ - __ x - xx - xx',
  replacement: { _: /\d/ },
};

const ForwardedInputMask = forwardRef<HTMLInputElement, InputMaskProps>(
  (props, forwardedRef) => {
    return (
      <InputMask
        ref={forwardedRef}
        mask={maskOptions.mask}
        replacement={maskOptions.replacement}
        showMask
        {...props}
      />
    );
  }
);

function EditForm({ doctor, goClaim, feedback, onChange }: IProps) {
  const [error, setError] = React.useState(EMPTY_ERROR);

  const [saving, setSaving] = React.useState(false);

  const { firstname, lastname, phone } = feedback;

  const handleSubmit = () => {
    setSaving(true);
    setError(EMPTY_ERROR);

    saveQrcodeFeedback(feedback)
      .then((result) => {
        if (result.status === API_RESPONSE_STATUS_CODE.CREATED) {
          window.location.replace(doctor.feedbackUrl);
        } else {
          setError({
            title: result?.error || 'Ошибка сервера',
            message: result?.message || 'Что то пошло не так',
          });
        }
      })
      .catch()
      .finally(() => setSaving(false));
  };

  const isValidForm = firstname && lastname && phone?.toString().length === 4;

  return (
    <>
      <CardContentWrap sx={{ padding: '32px 0 48px' }}>
        <Typography
          sx={{
            fontFamily: '"Nunito Sans", sans-serif',
            mb: 2,
          }}
          fontSize={18}
          lineHeight='25px'
          fontWeight={600}
        >
          Введите свое имя, фамилию<br/> и <b style={{fontSize: '20px'}}>4 цифры</b><br/> телефона, чтобы мы легко
          нашли Ваш отзыв
        </Typography>

        <Wrapper>
          <TextField
            id='firstname-input'
            label='Имя'
            variant='outlined'
            required
            value={firstname}
            onChange={(event) => onChange(Field.Firstname)(event.target.value)}
          />

          <TextField
            id='lastname-input'
            label='Фамилия'
            variant='outlined'
            required
            value={lastname}
            onChange={(event) => onChange(Field.Lastname)(event.target.value)}
          />

          <TextField
            slotProps={{
              input: {
                inputComponent: ForwardedInputMask,
              },
            }}
            id='phone-input'
            label='Телефон'
            variant='outlined'
            required
            onChange={(event) => {
              const digits = unformat(event.target.value, maskOptions);
              onChange(Field.Phone)(digits);
            }}
          />
        </Wrapper>

        <CardActions sx={{ justifyContent: 'flex-end', alignSelf: 'center' }}>
          <Button
            className='button success'
            disabled={!isValidForm}
            onClick={handleSubmit}
            loading={saving}
            variant='outlined'
          >
            Оставить отзыв {'>>'}
          </Button>

          <Button
            className='button-error-link error'
            disabled={!isValidForm}
            onClick={goClaim}
            loading={saving}
            variant='text'
          >
            Есть замечание
          </Button>
        </CardActions>
      </CardContentWrap>

      {error.title ? (
        <Snackbar
          open={!!error.title}
          onClose={() => setError(EMPTY_ERROR)}
          autoHideDuration={6000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ minWidth: '95%' }}
        >
          <Alert
            variant='filled'
            severity='error'
            sx={{
              width: '100%',
              '.MuiAlert-message': {
                textAlign: 'start',
              },
            }}
            onClose={() => setError(EMPTY_ERROR)}
          >
            <AlertTitle>{error.title}</AlertTitle>
            {error.message}
          </Alert>
        </Snackbar>
      ) : null}
    </>
  );
}

const CardContentWrap = styled(CardContent)`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-content: center;
`;

const Wrapper = styled('div')`
  align-self: stretch;
  flex: 1;
  display: grid;
  gap: 16px;
  padding: 16px 24px 32px;
`;

export default EditForm;
